/**
 * BLRRD Pipeline — buildPipelineContext
 *
 * Pure function. Takes the user-facing parameter objects (adjust / grade /
 * curves / curveIntensity) plus the curve-LUT helper, returns an object of
 * pre-computed constants that the inner render loop reads on every pixel.
 *
 * Why this is the first ESM seam:
 *  - Has no closure over `E` (state singleton). Pure inputs → pure output.
 *  - Inner loop in `blrrd-editor-v15.js` already destructures from a context
 *    object pattern; this just lifts the construction site out of the loop's
 *    file so it can be re-used by:
 *      • the future render Worker (Phase 1.B)
 *      • the .cube LUT bake (already exists, currently hot-swaps state)
 *      • golden-master pixel-hash regression tests
 *  - Pixel-hash gate verifies behavior is byte-identical before / after.
 *
 * Codex' "small canonical render core first" watch-out applies here: this is
 * the smallest unit that cleanly separates "config" from "execution".
 *
 * Phase 3.B-tone P7: optional `toneEq` field carries the 9-zone slider
 * state. The kernel does NOT consume it (kept inside the per-pixel loop
 * is the wrong layer for a whole-image guided-filter pre-pass). Instead
 * the renderer uses `toneEq.isIdentity` to decide whether to run the
 * pre-kernel `applyToneEqualizer` pass. When identity, the pixel-hash
 * + worker-parity baselines stay byte-identical.
 *
 * Phase 4.B.select.1: optional `selection` field carries the per-pixel
 * coverage mask. `selection.mask === null` means "everything selected"
 * — the kernel skips the per-pixel blend in that case (byte-identical
 * to pre-Wave-8 baselines). When non-null the kernel multiplies the
 * adjustment vs source via `mask[i>>2]` so unselected pixels stay
 * untouched and feathered edges blend smoothly.
 */

import { isIdentitySliders, sanitizeSliders, TONE_EQ_ZONES } from './tone-equalizer.js';
import { isIdentityStack, selectPixelLayers } from '../layers/model.js';

export function buildPipelineContext(adjust, grade, curves, curveIntensity, helpers, toneEqState, pixelLayers, selectionState) {
    const { buildCurveLUTF } = helpers;

    /* Phase 3.B-tone P7 — Tone Equalizer state. Identity by default
     * (all 9 sliders at 0 EV) so existing tests / baselines see no
     * behavioural change. The renderer reads `toneEq.isIdentity` to
     * skip the guided-filter pre-pass entirely. */
    const teSliders = sanitizeSliders(toneEqState && toneEqState.sliders);
    const teIdentity = isIdentitySliders(teSliders);
    const teRadius = (toneEqState && Number.isFinite(toneEqState.radius))
        ? toneEqState.radius : null;
    const teEps    = (toneEqState && Number.isFinite(toneEqState.eps))
        ? toneEqState.eps    : null;
    const toneEq = {
        sliders: teSliders,
        isIdentity: teIdentity,
        radius: teRadius,
        eps: teEps,
        zones: TONE_EQ_ZONES,
    };

    /* Phase 4.A.layers.1 — Pixel-layer composite stage. We only carry
     * a cheap `isIdentity` boolean + the filtered stack reference
     * through pipeCtx, NOT the layer Float32 buffers. The renderer
     * (or worker entry) reads `layers.stack` to drive the compositor.
     *
     * Why a boolean here:
     *   pipeCtx is structured-cloned across the worker boundary. If we
     *   embedded the per-layer Float32 RGBA in pipeCtx every frame
     *   would clone 64 MB per 4K layer. The renderer/worker pass the
     *   stack via a separate, transferable channel (Wave 7.2 follow-up:
     *   SAB-backed layer buffers + transferred list).
     *
     * Identity stack (empty / all hidden) triggers the compositor's
     * ref-passthrough fast path, keeping pixel-hash + worker-parity
     * baselines byte-identical when no pixel layers exist. */
    const pxStack = selectPixelLayers(pixelLayers);
    const layers = {
        stack: pxStack,
        isIdentity: isIdentityStack(pxStack),
        count: pxStack.length,
    };

    /* Float32 curve LUTs (1024 entries with interpolation) */
    const lutM = buildCurveLUTF(curves.master, curveIntensity.master);
    const lutR = buildCurveLUTF(curves.r,      curveIntensity.r);
    const lutG = buildCurveLUTF(curves.g,      curveIntensity.g);
    const lutB = buildCurveLUTF(curves.b,      curveIntensity.b);

    /* Pre-compute grade variables */
    const gr = grade;
    const gLiftR = gr.liftR,    gLiftG = gr.liftG,    gLiftB = gr.liftB,    gLiftY = gr.liftY;
    const gGammaR = gr.gammaR,  gGammaG = gr.gammaG,  gGammaB = gr.gammaB,  gGammaY = gr.gammaY;
    const gGainR = gr.gainR,    gGainG = gr.gainG,    gGainB = gr.gainB,    gGainY = gr.gainY;
    const gOffsetR = gr.offsetR, gOffsetG = gr.offsetG, gOffsetB = gr.offsetB, gOffsetM = gr.offsetMaster;
    const gCont = gr.gradeContrast, gPivot = gr.gradePivot;
    const gColorBoost = gr.gradeColorBoost, gSat = gr.gradeSaturation, gHue = gr.gradeHue, gLumMix = gr.gradeLumMix;
    const gShadows = gr.gradeShadows, gHighlights = gr.gradeHighlights;
    const gShadowsF = gShadows / 50, gHighlightsF = gHighlights / 50;
    const hasGrade =
        gLiftR !== 0 || gLiftG !== 0 || gLiftB !== 0 || gLiftY !== 0 ||
        gGammaR !== 0 || gGammaG !== 0 || gGammaB !== 0 || gGammaY !== 0 ||
        gGainR !== 0 || gGainG !== 0 || gGainB !== 0 || gGainY !== 0 ||
        gOffsetR !== 0 || gOffsetG !== 0 || gOffsetB !== 0 || gOffsetM !== 0 ||
        gCont !== 1.0 || gColorBoost !== 0 || gSat !== 50 || gHue !== 50 || gLumMix < 100 ||
        gShadows !== 0 || gHighlights !== 0;

    /* Pre-compute adjust constants — Float32 0..1 space */
    const a = adjust;
    const bright = a.brightness / 100;
    const contF  = a.contrast >= 0 ? 1 + a.contrast / 50 : 1 + a.contrast / 100;
    const temp   = a.temperature / 100;
    const tint   = a.tint / 100;
    const sat    = 1 + a.saturation / 100;
    const vib    = a.vibrance / 100;
    const hiAdj  = a.highlights * 1.5 / 255;
    const shAdj  = a.shadows    * 1.5 / 255;
    const whAdj  = a.whites     * 2.5 / 255;
    const blAdj  = a.blacks     * 2.5 / 255;
    const dehaze = a.dehaze / 100;
    const hasHsl = Object.keys(a).some(k => k.startsWith('hsl') && a[k] !== 0);
    /* Grade offsets in float space */
    const gOffRf = gOffsetR / 255, gOffGf = gOffsetG / 255, gOffBf = gOffsetB / 255, gOffMf = gOffsetM / 255;

    /* Phase 4.B.select.1 — Selection mask. null === everything selected.
     * The kernel reads `selection.mask` directly; when null the
     * post-loop blend is skipped entirely (zero per-pixel cost). The
     * pixel-hash + worker-parity baselines run with no selection set →
     * mask is null → byte-identical to pre-Wave-8 output. */
    const selMask = (selectionState && selectionState.mask instanceof Float32Array)
        ? selectionState.mask
        : null;
    const selection = {
        mask: selMask,
        isAllSelected: !selMask,
    };

    return {
        /* Selection (Phase 4.B.select.1) */
        selection,
        /* Curves */
        lutM, lutR, lutG, lutB,
        /* Grade — wheels */
        gLiftR, gLiftG, gLiftB, gLiftY,
        gGammaR, gGammaG, gGammaB, gGammaY,
        gGainR, gGainG, gGainB, gGainY,
        gOffsetR, gOffsetG, gOffsetB, gOffsetM,
        gOffRf, gOffGf, gOffBf, gOffMf,
        /* Grade — globals */
        gCont, gPivot, gColorBoost, gSat, gHue, gLumMix,
        gShadows, gHighlights, gShadowsF, gHighlightsF,
        hasGrade,
        /* Adjust */
        bright, contF, temp, tint, sat, vib,
        hiAdj, shAdj, whAdj, blAdj, dehaze,
        hasHsl,
        /* Tone Equalizer (Phase 3.B-tone P7) — pre-kernel pass */
        toneEq,
        /* Pixel-layer composite (Phase 4.A.layers.1) — post-kernel,
         * pre-view-transform stage. Renderer + worker read .isIdentity
         * to decide whether to allocate a composited buffer. */
        layers,
        /* Raw refs (loop reads a.hsl* directly for HSL-per-color) */
        a,
    };
}
